import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import API from '../api/axios';
import { AuthContext } from './AuthContext';

/**
 * Permissions context — wraps the app and fetches the current user's scope mask
 * from /cet-coordinators/me/permissions. Admins (superadmin/cetadmin) get every
 * scope implicitly; cetcoordinators get exactly what's been granted.
 *
 * Consumers call `hasScope(key)` to gate UI. Hard-coded scope strings are fine
 * because the same canonical list (utils/coordinatorScopes.js on the server)
 * is the source of truth.
 */
export const PermissionsContext = createContext({
  scopes: [],
  catalog: [],
  loading: false,
  implicit: false,
  hasScope: () => false,
  refresh: () => {}
});

export function PermissionsProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [scopes, setScopes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [implicit, setImplicit] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setScopes([]);
      setCatalog([]);
      setImplicit(false);
      return;
    }

    // Only roles that interact with the permission system fetch the mask.
    const gated = ['superadmin', 'cetadmin', 'cetcoordinator'];
    if (!gated.includes(user.role)) {
      setScopes([]);
      setCatalog([]);
      setImplicit(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await API.get('/cet-coordinators/me/permissions');
      setScopes(data.scopes || []);
      setCatalog(data.catalog || []);
      setImplicit(!!data.implicit);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setScopes([]);
      setCatalog([]);
      setImplicit(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const scopeSet = useMemo(() => new Set(scopes), [scopes]);
  const hasScope = useCallback((key) => implicit || scopeSet.has(key), [implicit, scopeSet]);

  const value = useMemo(() => ({
    scopes, catalog, loading, implicit, hasScope, refresh
  }), [scopes, catalog, loading, implicit, hasScope, refresh]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
